/**
 * usePodeEditar — hook utilitário pra RBAC granular dentro de páginas que
 * permitem múltiplos perfis com permissões diferentes (alguns CRUD, outros
 * read-only).
 *
 * Sprint R-IMPORT 2.x (2026-05-26).
 *
 * Uso:
 *   const podeEditar = usePodeEditar({ tela: 'contratos' });
 *   // dentro do JSX:
 *   {podeEditar && <Button>Editar</Button>}
 *
 * Matriz das telas R-IMPORT 2 (super-admin sempre passa):
 *   tela='catalogs-r2'           → administrativo
 *   tela='empresas-contratantes' → administrativo
 *   tela='contratos-empresa-contratante'      → administrativo
 *   tela='clientes'              → administrativo, comercial
 *   tela='responsaveis'          → administrativo, comercial
 *   tela='contratos'             → administrativo
 *
 * NÃO REVERTER: matriz canônica documentada também em ADR-015 (R-IMPORT 2).
 * Mudar matriz exige update do ADR e backend (middleware requireProfile).
 */

import { useAuth } from '@/contexts/AuthContext';

// 'empresas-contratantes' e 'contratos-empresa-contratante' saíram em 01/08 com
// o conceito de cliente terceirizado — as telas não existem mais.
// 'empresas-terceiras' FICA: é o cadastro das empreiteiras que a IGM contrata
// pra executar serviço. Nome parecido, negócio oposto.
export type TelaRImport2 =
  | 'catalogs-r2'
  // Faxina2 / FB2: cadastro das empreiteiras que a IGM contrata.
  | 'empresas-terceiras'
  | 'clientes'
  // RESPONSAVEIS-NA-FICHA (07/08): 'responsaveis' saiu — a tela morreu e a
  // função vive na ficha do cliente, sob a chave 'clientes'. Chave de permissão
  // sem tela faz o próximo leitor procurar uma tela que não existe.
  //
  // ⚠️ 'contratos' abaixo está na MESMA situação desde 06/08 (tela legada
  // removida na rodada ROTAS-ORFAS) e NÃO foi mexida aqui: a tabela e o
  // endpoint seguem servindo o Portal do Cliente, então a limpeza dela depende
  // de decidir o destino do módulo — é rodada própria, não sobra desta.
  | 'contratos';

const MATRIZ_PODE_EDITAR: Record<TelaRImport2, ReadonlyArray<string>> = {
  'catalogs-r2': ['administrativo'],
  'empresas-terceiras': ['administrativo'],
  clientes: ['administrativo', 'comercial'],
  contratos: ['administrativo'],
};

export function usePodeEditar({ tela }: { tela: TelaRImport2 }): boolean {
  const { user } = useAuth();
  if (!user) return false;

  // Super-admin (Matheus) sempre pode editar — bypass total de RBAC.
  if (user.isSuperAdmin) return true;

  const slug = user.profile?.slug;
  if (!slug) return false;
  return MATRIZ_PODE_EDITAR[tela].includes(slug);
}

/**
 * Versão pra rotas — retorna lista de slugs permitidos pra usar com
 * <RequireProfile slugs={[...]}>. Inclui slugs que podem APENAS LER
 * (comercial/tecnico podem visualizar /clientes mas só admin edita).
 *
 * Matriz de visualização (read OU CRUD):
 *   catalogs-r2/empresas-terceiras: administrativo only
 *   clientes: administrativo, comercial, tecnico (tecnico read-only)
 *   contratos: administrativo, comercial, tecnico (comercial+tecnico read-only)
 */
export const TELA_SLUGS_VISIVEIS: Record<TelaRImport2, ReadonlyArray<string>> = {
  'catalogs-r2': ['administrativo'],
  'empresas-terceiras': ['administrativo'],
  clientes: ['administrativo', 'comercial', 'tecnico'],
  contratos: ['administrativo', 'comercial', 'tecnico'],
};
